import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";

interface NavigationContextValue {
  isNavigationVisible: boolean;
  toggleNavigation: () => void;
  closeNavigation: () => void;
  openNavigation: () => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(
  undefined
);

export const NavigationProvider = ({ children }: { children: ReactNode }) => {
  const [isNavigationVisible, setIsNavigationVisible] = useState(false);

  const toggleNavigation = useCallback(() => {
    setIsNavigationVisible((prev) => !prev);
  }, []);

  const closeNavigation = useCallback(() => {
    setIsNavigationVisible(false);
  }, []);

  const openNavigation = useCallback(() => {
    setIsNavigationVisible(true);
  }, []);

  const value = useMemo(
    () => ({
      isNavigationVisible,
      toggleNavigation,
      closeNavigation,
      openNavigation,
    }),
    [isNavigationVisible, toggleNavigation, closeNavigation, openNavigation]
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextValue => {
  const ctx = useContext(NavigationContext);
  if (!ctx)
    throw new Error("useNavigation must be used within NavigationProvider");
  return ctx;
};

export default NavigationContext;
